import type { LibKey } from '@generated/models';

export const LIB_LABEL: Record<LibKey, string> = { itp: 'ITP Hockey Canada', sd: 'Sport Dispute' };

export interface Decision {
  key: string;
  lib: LibKey;
  id: string;
  name: string;
  group: string;
  document: string;
  sport: string;
  process: string;
  adjudicator: string;
  level: string;
  province: string;
  jurisdiction: string[];
  date: string | null;
  complaintType: string[];
  summary: string;
  keywords: string[];
  finding: string[];
  mitigating: string[];
  aggravating: string[];
  sanctions: string[];
  suspensionDuration: string;
  educationalRequirement: string;
  severityWarning: string;
  substantiated: string[];
  unsubstantiated: string[];
  notes: string;
  respondentName: string;
  respondentAge: string;
  respondentRole: string[];
  respondentGender: string[];
  anonymous: string;
  caseManager: string[];
  fileId: string;
  published: boolean;
}

export function stripHtml(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

export function hasText(v: unknown): boolean {
  return stripHtml(v).length > 0 && !/^n\/?a$/i.test(stripHtml(v));
}

export function formatSanctions(d: Pick<Decision, 'sanctions' | 'suspensionDuration' | 'educationalRequirement'>): string[] {
  return d.sanctions.map((s) => {
    if (/suspension/i.test(s) && !/no (additional )?suspension/i.test(s) && hasText(d.suspensionDuration)) return `${s} (${d.suspensionDuration})`;
    if (/educat/i.test(s) && hasText(d.educationalRequirement)) return `${s} (${d.educationalRequirement})`;
    return s;
  });
}

export function normaliseSanction(value: string): string | null {
  const t = stripHtml(value).toLowerCase();
  if (!t) return null;
  if (/no additional suspension/.test(t)) return 'No Additional Suspension Imposed';
  if (/no sanctions? imposed|^n\/?a$/.test(t)) return 'No Sanction Imposed';
  if (/permanent (ineligib|eligibility restriction)/.test(t)) return 'Permanent Ineligibility';
  if (/contact restriction/.test(t)) return 'Contact Restrictions';
  if (/behavio(u)?ral restriction/.test(t)) return 'Behavioural Restrictions';
  if (/costs? award/.test(t)) return 'Costs Award';
  if (/other discretionary sanction/.test(t)) return 'Other Discretionary Sanctions';
  if (/other required outcome|community service|counselling|assessment|mentoring|supervision/.test(t)) return 'Other Required Outcomes';
  if (/apolog/.test(t)) return 'Formal Apology';
  if (/warning|reprimand/.test(t)) return 'Verbal or Written Warning';
  if (/probation/.test(t)) return 'Probation';
  if (/eligib|restriction/.test(t)) return 'Eligibility Restrictions';
  if (/educat|course|training|workshop|program/.test(t)) return 'Education';
  if (/\bfine\b|monetary/.test(t)) return 'Fine';
  if (/suspen/.test(t)) return 'Suspension';
  return null;
}

export function sanctionCategories(d: Decision): string[] {
  const set = new Set<string>();
  d.sanctions.forEach((s) => {
    const c = normaliseSanction(s);
    if (c) set.add(c);
  });
  if (hasText(d.educationalRequirement)) set.add('Education');
  if (hasText(d.suspensionDuration) && !set.has('No Additional Suspension Imposed') && !set.has('No Sanction Imposed')) set.add('Suspension');
  return [...set];
}

export function normaliseFinding(values: string[]): string {
  const f = values.join(' ').toLowerCase();
  if (!f) return 'Other / Not recorded';
  if (f.includes('partial') && f.includes('substantiat')) return 'Partially Substantiated';
  if (f.includes('unsubstantiat') || f.includes('not substantiat') || f.includes('no finding')) return 'Unsubstantiated';
  if (f.includes('substantiat')) return 'Substantiated';
  return 'Other / Not recorded';
}

export function reportingPeriod(date: string | null): string {
  if (!date) return 'Undated';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return 'Undated';
  const start = d.getUTCMonth() >= 6 ? d.getUTCFullYear() : d.getUTCFullYear() - 1;
  return `${start}-${start + 1}`;
}

export function percentage(count: number, total: number): string {
  if (!total) return '0%';
  const v = (count / total) * 100;
  return `${v >= 10 ? v.toFixed(0) : v.toFixed(1)}%`;
}

export function fmtDate(date: string | null): string {
  if (!date) return '—';
  const d = new Date(date);
  return Number.isNaN(d.getTime()) ? '—' : d.toISOString().slice(0, 10);
}

export function completeness(d: Decision): number {
  return [d.finding.length > 0, d.complaintType.length > 0, !!d.date, hasText(d.summary), d.sanctions.length > 0].filter(Boolean).length;
}
